/**
 * InfiniteBanking — Wealth for Life Blueprint.
 *
 * Three tabs:
 *   1. The Playbook            — concept + 15 EXODUS phrases + why it works
 *   2. Policy Layering         — 35 age×tier audio cards + non-lapse rules
 *   3. Wealth for Life Blueprint — calculator + booking CTA
 *
 * Same diction / voice as the WealthImplementation page. Booking CTA
 * routes into the existing /reserve $100 refundable deposit flow.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, API_BASE } from "../lib/api";
import {
  ArrowRight, Play, Pause, Spinner, Bank, TreeStructure, Vault,
  ChartLineUp, Lock, CalendarCheck, Sparkle,
} from "@phosphor-icons/react";

const TIER_ORDER = ["t1", "t2", "t3", "t4", "t5"];
const TIER_META = {
  t1: { label: "Working Wealth",   range: "$40K – $150K",  color: "#8C6E37" },
  t2: { label: "Rising Pro",       range: "$180K – $250K", color: "#A6884A" },
  t3: { label: "Established Pro",  range: "$280K – $450K", color: "#C5A059" },
  t4: { label: "Executive Class",  range: "$500K – $750K", color: "#D9BB6D" },
  t5: { label: "Capital Owner",    range: "$780K+",        color: "#EED690" },
};

/* ═════════════════════════════ TAB 1 — THE PLAYBOOK ═════════════════════════ */
function TabPlaybook() {
  const phrases = [
    "Become your own bank.",
    "Recapture the interest you'd otherwise pay to a stranger.",
    "Your capital's supposed to work for you twice.",
    "The policy is the vault. The loan is the tool.",
    "You don't withdraw. You leverage.",
    "Every dollar you earn is either a soldier or a slave.",
    "The pool of capital that finances your life.",
    "Compound in one place, deploy from another.",
    "No permission slips. No credit committees.",
    "The family bank starts the day the first policy funds.",
    "We're building generational velocity, not just a retirement plan.",
    "Your paycheck is a temporary asset. The policy is the permanent one.",
    "The interest you charge yourself pays you back.",
    "Policy loans don't touch your cash value — they multiply it.",
    "The point isn't to die rich. It's to live rich and leave a bank.",
  ];
  return (
    <div data-testid="ibc-tab-playbook" className="space-y-16">
      {/* Hero */}
      <section className="max-w-4xl">
        <p className="text-exodus-gold-deep text-xs tracking-[0.2em] uppercase font-semibold">
          The Wealth for Life Blueprint
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-exodus-gold leading-tight">
          How wealthy families actually stay wealthy across generations.
        </h2>
        <p className="mt-6 text-exodus-text/85 text-lg leading-relaxed">
          They didn’t invest their way to a hundred-year dynasty. They banked
          their way to it. Every dollar the family ever earned flowed through
          a policy they owned first, before it ever touched a public bank.
          When they needed capital for a business, a property, a foundation,
          they borrowed it from themselves and repaid themselves with interest.
          That’s the whole game.
        </p>
        <p className="mt-4 text-exodus-text/70 leading-relaxed">
          The Wealth for Life Blueprint is that same architecture, designed
          for your income, your age, your family, your goals. Dividend-paying
          Whole Life as the core capital reservoir. At higher tiers, IUL layered
          alongside as the tax-free income wing. A policy-layering rhythm so
          it compounds for the next generation before you ever draw from it.
          Every audio below teaches you exactly how to implement the play at
          your stage. No fluff. Just moves.
        </p>
      </section>

      {/* Mechanics — three-panel */}
      <section className="grid gap-6 md:grid-cols-3">
        {[
          {
            icon: Vault, title: "The Vault",
            body: "Dividend-paying Whole Life from a top mutual carrier. Guaranteed cash value, guaranteed premium, guaranteed death benefit, PLUS annual dividends from a company that's paid them 100+ years running. The policy is the vault. Everything else is scaffolding.",
          },
          {
            icon: TreeStructure, title: "The Design",
            body: "Base premium plus a Paid-Up Additions (PUA) rider. The rider is where the magic lives — it's how we soak the policy with liquid cash value inside the first twelve months while staying non-MEC. Depending on your tier, we run 40/60 up to 15/85 base/PUA splits.",
          },
          {
            icon: Bank, title: "The Loop",
            body: "You need capital? You loan against the cash value. Your money in the policy keeps compounding as if you never touched it, because you didn't touch it — you borrowed against it. You repay yourself with interest, on your schedule. No credit check. No permission slip. That's the loop.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-exodus-gold-deep/50 bg-exodus-panel p-6"
               data-testid={`ibc-mechanic-${title.replace(/\s/g,'-').toLowerCase()}`}>
            <Icon size={28} weight="light" className="text-exodus-gold" />
            <h3 className="mt-4 text-lg font-bold text-exodus-gold">{title}</h3>
            <p className="mt-3 text-exodus-text/80 text-sm leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      {/* 15 EXODUS phrases */}
      <section>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-exodus-gold-deep text-xs tracking-[0.18em] uppercase font-semibold">
              The Vocabulary
            </p>
            <h3 className="mt-2 text-2xl font-bold text-exodus-gold">
              Fifteen lines that separate bankers from borrowers.
            </h3>
          </div>
          <p className="text-exodus-text/60 text-sm max-w-md">
            Learn these. When you catch yourself thinking any of them mid-day,
            you’re already thinking like the wealthy families we build for.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {phrases.map((p, i) => (
            <div key={i} data-testid={`ibc-phrase-${i}`}
                 className="rounded-xl border border-exodus-gold-deep/40 bg-exodus-surface p-5">
              <p className="text-exodus-gold text-xs font-mono tracking-wider">{String(i+1).padStart(2,'0')}</p>
              <p className="mt-2 text-exodus-text/90 leading-snug">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Big booking CTA */}
      <BookingCTA prompt="Ready to design your own bank?" />
    </div>
  );
}

/* ═════════════════════════════ TAB 2 — POLICY LAYERING ═════════════════════ */
function AudioCard({ bucket, activeKey, setActiveKey }) {
  const [selectedTier, setSelectedTier] = useState("t2");
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);

  useEffect(() => {
    const ourKey = `${bucket.id}::${selectedTier}`;
    if (activeKey !== ourKey && audioRef.current) {
      audioRef.current.pause();
      setState((s) => (s === "playing" ? "paused" : s));
    }
  }, [activeKey, bucket.id, selectedTier]);

  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setState("idle");
  }, [selectedTier]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const play = () => {
    const key = `${bucket.id}::${selectedTier}`;
    if (state === "playing" && audioRef.current) { audioRef.current.pause(); setState("paused"); return; }
    if (state === "paused" && audioRef.current) {
      audioRef.current.play().then(() => { setState("playing"); setActiveKey(key); })
        .catch(() => setState("idle"));
      return;
    }
    setState("loading");
    setActiveKey(key);
    const url = `${API_BASE}/infinite-banking/${bucket.id}/${selectedTier}/audio`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onplaying = () => setState("playing");
    audio.onpause = () => setState((s) => (s === "playing" ? "paused" : s));
    audio.onended = () => setState("idle");
    audio.onerror = () => setState("idle");
    audio.play().catch(() => setState("idle"));
  };

  return (
    <div data-testid={`ibc-card-${bucket.id}`}
         className="rounded-2xl border border-exodus-gold-deep/50 bg-exodus-panel p-6 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-exodus-gold-deep text-xs tracking-wider">AGE {bucket.label}</p>
          <p className="mt-1 text-exodus-gold font-semibold">{bucket.life_stage}</p>
        </div>
        <Bank size={22} className="text-exodus-gold/60" weight="light" />
      </div>
      <p className="mt-3 text-exodus-text/70 text-sm italic leading-snug">{bucket.tagline}</p>

      <div className="mt-4">
        <p className="text-xs text-exodus-text/60 mb-2">Choose income tier:</p>
        <div className="flex flex-wrap gap-1.5">
          {TIER_ORDER.map((t) => (
            <button key={t}
                    data-testid={`ibc-tier-${bucket.id}-${t}`}
                    onClick={() => setSelectedTier(t)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                      selectedTier === t
                        ? "border-exodus-gold bg-exodus-gold/15 text-exodus-gold"
                        : "border-exodus-gold-deep/40 text-exodus-text/60 hover:text-exodus-text/90"
                    }`}>
              {TIER_META[t].label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-exodus-text/50">
          {TIER_META[selectedTier].label} · {TIER_META[selectedTier].range}
        </p>
      </div>

      <button data-testid={`ibc-play-${bucket.id}`}
              onClick={play}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-black transition text-sm font-semibold self-start">
        {state === "loading" ? <><Spinner size={16} className="animate-spin" /> Loading…</> :
         state === "playing" ? <><Pause size={16} weight="fill" /> Pause the play</> :
         state === "paused"  ? <><Play size={16} weight="fill" /> Resume</> :
                                <><Play size={16} weight="fill" /> Play the play</>}
      </button>
    </div>
  );
}

function TabLayering({ buckets }) {
  const [activeKey, setActiveKey] = useState(null);
  const lapseRules = [
    "Automatic monthly bank draft — set it and forget it. Never pay premium by hand.",
    "PUA rider paid annually (not monthly). One lean month can't stall the plan.",
    "Keep six months of premium PARKED inside the policy itself as a float.",
    "Annual policy review, same month every year — Q1 works best. Never skipped.",
    "At tier 3 and up: separate 'premium reserve account' funded quarterly.",
    "At tier 4 and up: family-bank board meets quarterly, reserve = 24 months.",
    "At tier 5: trust-funded reserve, annual actuarial review, rate-shock modeled.",
  ];
  return (
    <div data-testid="ibc-tab-layering" className="space-y-12">
      <section className="max-w-3xl">
        <p className="text-exodus-gold-deep text-xs tracking-[0.2em] uppercase font-semibold">
          Layering &amp; Never-Lapse
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-exodus-gold leading-tight">
          The order matters. The rhythm matters. The guardrails matter more.
        </h2>
        <p className="mt-5 text-exodus-text/85 leading-relaxed">
          A poorly designed IBC policy is worse than no policy — you’re paying
          premiums into a structure that never gets liquid. A properly designed
          policy is liquid inside twelve months and starts financing your life
          by year two. Below, the 35 age × tier plays are stacked so you can
          hear exactly how it looks at your stage.
        </p>
      </section>

      <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {(buckets || []).map((b) => (
          <AudioCard key={b.id} bucket={b} activeKey={activeKey} setActiveKey={setActiveKey} />
        ))}
      </section>

      <section>
        <div className="flex items-center gap-3 mb-4">
          <Lock size={22} className="text-exodus-gold" weight="light" />
          <h3 className="text-2xl font-bold text-exodus-gold">The Non-Lapse Rules</h3>
        </div>
        <p className="text-exodus-text/70 mb-6 max-w-3xl">
          These are the guardrails we install on every policy. Non-negotiable.
          A lapsed policy is a lost bank — and we’re not building a plan that
          your grandkids inherit as a canceled contract.
        </p>
        <ol className="space-y-3 max-w-3xl">
          {lapseRules.map((r, i) => (
            <li key={i} data-testid={`ibc-lapse-rule-${i}`}
                className="flex gap-4 rounded-xl border border-exodus-gold-deep/40 bg-exodus-surface p-4">
              <span className="text-exodus-gold font-bold text-lg leading-none pt-0.5">{i+1}</span>
              <span className="text-exodus-text/90 leading-relaxed">{r}</span>
            </li>
          ))}
        </ol>
      </section>

      <BookingCTA prompt="Want your exact layering plan on paper?" />
    </div>
  );
}

/* ═════════════════════════════ TAB 3 — BLUEPRINT CALCULATOR ═════════════════ */
function TabBlueprint() {
  const [form, setForm] = useState({
    name: "", email: "",
    age: 35, income: 220000, monthly_capacity: 1200,
    primary_goal: "Financial freedom and family capital",
    has_spouse: true, has_kids: false, consent_to_contact: true,
  });
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/infinite-banking/lead", form);
      setRec(data.recommendation);
      // scroll to the recommendation
      setTimeout(() => {
        document.getElementById("ibc-blueprint-result")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Something's off. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ibc-tab-blueprint" className="space-y-12">
      <section className="max-w-3xl">
        <p className="text-exodus-gold-deep text-xs tracking-[0.2em] uppercase font-semibold">
          Your Blueprint
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-exodus-gold leading-tight">
          Give us six numbers. We’ll give you your policy stack.
        </h2>
        <p className="mt-5 text-exodus-text/85 leading-relaxed">
          Age, income, capacity, family situation, primary goal. We run it
          through the same recommendation engine we use in the private
          Blueprint call. You’ll see your tier, your base/PUA split, your
          policy stack sequence, your reservoir projection at year 10, year 20,
          and by the third generation.
        </p>
      </section>

      <section className="grid gap-8 lg:grid-cols-2">
        <form onSubmit={submit} data-testid="ibc-blueprint-form"
              className="rounded-2xl border border-exodus-gold-deep/50 bg-exodus-panel p-6 space-y-5">
          <div>
            <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Your name</label>
            <input required data-testid="ibc-input-name"
                   value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none" />
          </div>
          <div>
            <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Email</label>
            <input required type="email" data-testid="ibc-input-email"
                   value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Age</label>
              <input required type="number" min="16" max="95" data-testid="ibc-input-age"
                     value={form.age}
                     onChange={(e) => setForm({ ...form, age: +e.target.value || 0 })}
                     className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none" />
            </div>
            <div>
              <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Annual income ($)</label>
              <input required type="number" min="0" step="1000" data-testid="ibc-input-income"
                     value={form.income}
                     onChange={(e) => setForm({ ...form, income: +e.target.value || 0 })}
                     className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Monthly funding capacity ($)</label>
            <input required type="number" min="0" step="50" data-testid="ibc-input-capacity"
                   value={form.monthly_capacity}
                   onChange={(e) => setForm({ ...form, monthly_capacity: +e.target.value || 0 })}
                   className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none" />
            <p className="mt-1 text-xs text-exodus-text/50">
              How much can realistically go into the policy each month? Be honest — this drives the design.
            </p>
          </div>
          <div>
            <label className="text-xs text-exodus-text/70 uppercase tracking-wider">Primary goal</label>
            <select data-testid="ibc-input-goal"
                    value={form.primary_goal}
                    onChange={(e) => setForm({ ...form, primary_goal: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text focus:border-exodus-gold outline-none">
              <option>Financial freedom and family capital</option>
              <option>Retirement income (tax-free)</option>
              <option>Home ownership / real estate investing</option>
              <option>Business capital / entrepreneurship</option>
              <option>Kids &amp; grandkids education / first homes</option>
              <option>Estate &amp; legacy planning</option>
              <option>Debt collapse through internal financing</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm text-exodus-text/85">
              <input type="checkbox" data-testid="ibc-input-spouse"
                     checked={form.has_spouse}
                     onChange={(e) => setForm({ ...form, has_spouse: e.target.checked })} />
              Married / partnered
            </label>
            <label className="flex items-center gap-2 text-sm text-exodus-text/85">
              <input type="checkbox" data-testid="ibc-input-kids"
                     checked={form.has_kids}
                     onChange={(e) => setForm({ ...form, has_kids: e.target.checked })} />
              Have children
            </label>
          </div>
          {err && <p className="text-red-400 text-sm" data-testid="ibc-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="ibc-blueprint-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-exodus-gold text-black font-bold hover:bg-exodus-gold/90 disabled:opacity-60">
            {busy ? <><Spinner size={16} className="animate-spin" /> Building…</> : <>Build my Blueprint <ArrowRight size={16} weight="bold" /></>}
          </button>
          <p className="text-[11px] text-exodus-text/50 text-center">
            Your information is saved so we can prep for your Blueprint call. Never shared. Never sold.
          </p>
        </form>

        {/* Right column — teaser what they'll see */}
        <div className="rounded-2xl border border-exodus-gold-deep/40 bg-exodus-surface p-6">
          <p className="text-exodus-gold-deep text-xs tracking-wider uppercase">What you&apos;ll see</p>
          <h4 className="mt-2 text-xl font-bold text-exodus-gold">Your personal policy stack</h4>
          <ul className="mt-4 space-y-3 text-sm text-exodus-text/85">
            {[
              "The exact tier you slot into (Working Wealth → Capital Owner)",
              "Your base / PUA split, engineered for liquidity year one",
              "The order of policies to open and when (policy #1, #2, #3…)",
              "Real capital-use cases — car, house, business, kids, retirement",
              "Reservoir projection at year 10, year 20, and by the third generation",
              "The exact guardrails so this plan never lapses",
            ].map((x, i) => (
              <li key={i} className="flex gap-3">
                <ChartLineUp size={16} className="text-exodus-gold mt-0.5 shrink-0" />
                {x}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-exodus-text/60 italic">
            The Blueprint below is your starting point. The private consult is
            where we design the actual policy — carrier, riders, riders on riders,
            insurability, funding source, layering timeline. That’s the room where
            the bank actually gets built.
          </p>
        </div>
      </section>

      {/* Recommendation output */}
      {rec && <RecommendationView rec={rec} />}
    </div>
  );
}

function RecommendationView({ rec }) {
  return (
    <section id="ibc-blueprint-result" data-testid="ibc-blueprint-result"
             className="rounded-3xl border border-exodus-gold bg-gradient-to-br from-exodus-panel to-exodus-surface p-8 space-y-8 mt-4">
      <header>
        <p className="text-exodus-gold-deep text-xs tracking-[0.2em] uppercase font-semibold">
          Your Blueprint · Tier {rec.tier} · {rec.tier_label}
        </p>
        <h3 className="mt-2 text-3xl font-bold text-exodus-gold">
          Here’s your policy stack.
        </h3>
      </header>

      <div className="space-y-3">
        {rec.summary_lines.map((s, i) => (
          <p key={i} data-testid={`ibc-summary-${i}`} className="text-exodus-text/90 leading-relaxed">
            {s}
          </p>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-exodus-bg/40 border border-exodus-gold-deep/40 p-5">
          <p className="text-exodus-gold-deep text-xs tracking-wider uppercase mb-3">Policy stack (in order)</p>
          <ol className="space-y-3">
            {rec.policy_stack.map((p, i) => (
              <li key={i} data-testid={`ibc-policy-${i}`} className="flex gap-3 text-sm">
                <span className="text-exodus-gold font-bold w-6">{p.seq}.</span>
                <div>
                  <p className="text-exodus-text"><span className="font-bold text-exodus-gold">{p.type}</span> · <span className="text-exodus-text/70">{p.when}</span></p>
                  <p className="text-exodus-text/75 text-xs">Insured: {p.insured}</p>
                  <p className="text-exodus-text/60 text-xs mt-1 italic">{p.role}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl bg-exodus-bg/40 border border-exodus-gold-deep/40 p-5">
          <p className="text-exodus-gold-deep text-xs tracking-wider uppercase mb-3">Reservoir projection</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Year 10", rec.dynasty_projection.year_10],
              ["Year 20", rec.dynasty_projection.year_20],
              ["Next generation", rec.dynasty_projection.gen_2],
              ["Grandkids", rec.dynasty_projection.gen_3],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-exodus-surface p-3 border border-exodus-gold-deep/30">
                <p className="text-[10px] tracking-wider uppercase text-exodus-text/60">{k}</p>
                <p className="mt-1 text-exodus-gold font-bold">{v}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-exodus-text/60">
            Base / PUA split: <span className="text-exodus-gold font-semibold">
              {rec.base_pua_split.base_pct}% base / {rec.base_pua_split.pua_pct}% PUA
            </span>
          </p>
          <p className="mt-1 text-xs text-exodus-text/60">
            Capacity check: <span className="text-exodus-gold">{rec.capacity_score}</span>
          </p>
        </div>

        <div className="rounded-2xl bg-exodus-bg/40 border border-exodus-gold-deep/40 p-5">
          <p className="text-exodus-gold-deep text-xs tracking-wider uppercase mb-3">What you&apos;ll finance from your bank</p>
          <ul className="space-y-2 text-sm text-exodus-text/85">
            {rec.capital_use_cases.map((u, i) => (
              <li key={i} className="flex gap-2">
                <Sparkle size={12} className="text-exodus-gold mt-1 shrink-0" weight="fill" />
                {u}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-exodus-bg/40 border border-exodus-gold-deep/40 p-5">
          <p className="text-exodus-gold-deep text-xs tracking-wider uppercase mb-3">Never-lapse guardrails</p>
          <ul className="space-y-2 text-sm text-exodus-text/85">
            {rec.lapse_guardrails.map((g, i) => (
              <li key={i} className="flex gap-2">
                <Lock size={12} className="text-exodus-gold mt-1 shrink-0" />
                {g}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Final push to book */}
      <BookingCTA prompt="Ready to design the actual policy?" bold />
    </section>
  );
}

/* ═════════════════════════════ BOOKING CTA (shared) ═════════════════════════ */
function BookingCTA({ prompt, bold = false }) {
  return (
    <section data-testid="ibc-booking-cta"
             className={`rounded-3xl border ${bold ? "border-exodus-gold" : "border-exodus-gold-deep/60"} bg-gradient-to-r from-exodus-panel via-exodus-surface to-exodus-panel p-8 sm:p-10`}>
      <div className="grid gap-6 md:grid-cols-[1fr_auto] items-center">
        <div>
          <p className="text-exodus-gold-deep text-xs tracking-[0.2em] uppercase font-semibold">
            Wealth for Life Blueprint Call
          </p>
          <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-exodus-gold">
            {prompt}
          </h3>
          <p className="mt-3 text-exodus-text/85 leading-relaxed max-w-2xl">
            Thirty minutes. One-on-one. A one hundred dollar refundable
            deposit that credits back to your first premium. We design your
            actual policy — carrier, base/PUA split, riders, layering timeline,
            never-lapse structure. This is the room where the bank actually
            gets built.
          </p>
          <ul className="mt-4 text-sm text-exodus-text/70 space-y-1">
            <li className="flex items-center gap-2"><CalendarCheck size={14} className="text-exodus-gold" /> Book on your calendar — no chase</li>
            <li className="flex items-center gap-2"><Lock size={14} className="text-exodus-gold" /> Refundable if we&apos;re not the fit</li>
            <li className="flex items-center gap-2"><Sparkle size={14} className="text-exodus-gold" weight="fill" /> Your Blueprint gets pre-loaded before you walk in</li>
          </ul>
        </div>
        <Link to="/reserve"
              data-testid="ibc-cta-reserve"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-exodus-gold text-black font-bold hover:bg-exodus-gold/90 transition text-base whitespace-nowrap">
          Reserve the call <ArrowRight size={18} weight="bold" />
        </Link>
      </div>
    </section>
  );
}

/* ═════════════════════════════ PAGE SHELL ═════════════════════════════════════ */
export default function InfiniteBanking() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["playbook", "layering", "blueprint"].includes(t) ? t : "playbook";
  });
  const [buckets, setBuckets] = useState([]);

  useEffect(() => {
    api.get("/infinite-banking")
      .then(({ data }) => setBuckets(data.buckets || []))
      .catch(() => setBuckets([]));
  }, []);

  const tabs = useMemo(() => ([
    { id: "playbook",  label: "The Playbook",              icon: Bank },
    { id: "layering",  label: "Policy Layering",           icon: TreeStructure },
    { id: "blueprint", label: "Wealth for Life Blueprint", icon: Vault },
  ]), []);

  return (
    <div data-testid="infinite-banking" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Page header */}
        <header className="mb-10">
          <p className="text-exodus-gold-deep text-xs tracking-[0.24em] uppercase font-semibold">
            Wealth for Life Blueprint
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-exodus-gold leading-[1.05]">
            Own your bank.<br/>Finance your own life.
          </h1>
          <p className="mt-5 text-lg text-exodus-text/80 max-w-3xl leading-relaxed">
            This is the layer that teaches you how to become your own
            capital reservoir — the same architecture wealthy families of
            the last century used to hold their capital across generations
            without ever renting it back from a public bank.
          </p>
        </header>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-10 border-b border-exodus-gold-deep/30" data-testid="ibc-tab-bar">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id}
                    data-testid={`ibc-tab-${id}`}
                    onClick={() => setTab(id)}
                    className={`inline-flex items-center gap-2 px-5 py-3 border-b-2 text-sm font-semibold transition ${
                      tab === id
                        ? "border-exodus-gold text-exodus-gold"
                        : "border-transparent text-exodus-text/60 hover:text-exodus-text"
                    }`}>
              <Icon size={16} weight={tab === id ? "fill" : "light"} />
              {label}
            </button>
          ))}
        </div>

        {tab === "playbook"  && <TabPlaybook />}
        {tab === "layering"  && <TabLayering buckets={buckets} />}
        {tab === "blueprint" && <TabBlueprint />}
      </main>
      <Footer />
    </div>
  );
}
